import { type Film, typeLabel } from '@/mocks/films';

interface Props {
  film: Film;
  onPreview: () => void;
  favorite: boolean;
  onToggleFavorite: () => void;
  onShare: () => void;
  shareTip: string;
}

export default function DetailHero({
  film,
  onPreview,
  favorite,
  onToggleFavorite,
  onShare,
  shareTip,
}: Props) {
  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="absolute inset-0">
        <img
          src={film.backdrop}
          alt={`${film.title}剧照`}
          title={`${film.title}剧情介绍 - 同学的妈妈`}
          className="h-full w-full object-cover object-top opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/80 to-foreground-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/20 to-transparent" />
      </div>

      <div className="relative z-10 px-4 py-8 md:px-8 md:py-12 lg:px-12">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-8">
          <button
            type="button"
            onClick={onPreview}
            aria-label="查看海报大图"
            className="group relative w-32 shrink-0 overflow-hidden rounded-lg md:w-48 cursor-pointer"
          >
            <div className="aspect-[2/3] w-full">
              <img
                src={film.poster}
                alt={`${film.title}海报`}
                title={`${film.title}剧情介绍 - 同学的妈妈`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <span className="absolute inset-0 flex items-center justify-center bg-foreground-950/45 opacity-0 transition-opacity group-hover:opacity-100">
              <i className="ri-zoom-in-line text-2xl text-background-50 w-6 h-6 flex items-center justify-center" />
            </span>
          </button>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-accent-500 px-2 py-1 font-label text-[11px] font-bold text-foreground-950">
                {typeLabel[film.type]}
              </span>
              <span className="rounded-md border border-background-50/30 px-2 py-1 font-label text-[11px] text-background-100">
                {film.region}
              </span>
              <span className="rounded-md border border-background-50/30 px-2 py-1 font-label text-[11px] text-background-100">
                {film.year}
              </span>
              <span className="rounded-md border border-background-50/30 px-2 py-1 font-label text-[11px] text-background-100">
                {film.language}
              </span>
              <span className="flex items-center gap-1 rounded-md bg-secondary-500/25 px-2 py-1 font-label text-[11px] text-secondary-100">
                <i className="ri-history-line w-3 h-3 flex items-center justify-center" />
                已加入浏览记录
              </span>
            </div>

            <h1 className="mt-3 font-heading text-2xl font-black leading-snug text-background-50 md:text-4xl">
              {film.title}
            </h1>
            <p className="mt-1 font-label text-[11px] uppercase tracking-[0.14em] text-background-300 md:text-xs">
              {film.originalTitle}
            </p>

            <div className="mt-3.5 flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="flex items-baseline gap-1 font-label">
                <span className="text-2xl font-black text-accent-400">{film.rating.toFixed(1)}</span>
                <span className="text-xs text-background-300">/ 10</span>
              </span>
              <span className="text-xs text-background-200">
                {film.releaseDate} 上映 · {film.duration}
              </span>
            </div>

            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-100/90">
              {film.summary}
            </p>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {film.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/category/${film.type}?genre=${encodeURIComponent(genre)}`}
                  className="rounded-md bg-background-50/10 px-2 py-1 text-[11px] text-background-100 transition-colors hover:bg-background-50/20 cursor-pointer whitespace-nowrap"
                >
                  {genre}
                </Link>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={onToggleFavorite}
                className={`inline-flex h-10 items-center gap-1.5 rounded-md px-4 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  favorite
                    ? 'bg-accent-500 text-foreground-950 hover:bg-accent-400'
                    : 'bg-background-50/12 text-background-50 hover:bg-background-50/20 border border-background-50/30'
                }`}
              >
                <i
                  className={`${favorite ? 'ri-heart-fill' : 'ri-heart-line'} w-4 h-4 flex items-center justify-center`}
                />
                {favorite ? '已收藏' : '收藏本片'}
              </button>
              <button
                type="button"
                onClick={onShare}
                className="inline-flex h-10 items-center gap-1.5 rounded-md border border-background-50/30 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center" />
                分享链接
              </button>
              {shareTip && (
                <span className="font-label text-[11px] text-accent-300 anim-fade-in">{shareTip}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}